import { CircularProgress, TableCell, makeStyles } from "@material-ui/core";
import moment from "moment";
import { useSnackbar } from "notistack";
import React, { useState } from "react";

import { Employee } from "../../../dtos/employee";
import { AjaxResult } from "../../../enums/ajaxResult";
import { UserLevel } from "../../../enums/userLevel";
import { SortInfo } from "../../../rest/requestObjects/SortInfo";
import { useAuth } from "../../../features/auth/context";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { useApi } from "../../useApi";
import { ResponsiveDataView, Column } from "../../shared/ResponsiveDataView";
import { RowActionsMenu, RowAction } from "../../shared/RowActionsMenu";
import { TableCellWithSort } from "../../TableCellWithSort";

import { EmployeeEditForm } from "./editForm";
import { EnrollEmployeeButton } from "./EnrollEmployeeButton";
import { useSiteColumn } from "./useSiteColumn";

interface Props {
    dealerId: number | undefined;
    orgId: number | undefined;
    siteId: number | undefined;
    employees: Employee[];
    onLoadEmployees: () => void;
    sortInfo: SortInfo | undefined;
    setSortInfo: (sortInfo: SortInfo) => void;
    isFederatedSsoEnabled: boolean;
    isLoading?: boolean;
}

export function EmployeesTable({
    dealerId,
    orgId,
    siteId,
    employees,
    onLoadEmployees,
    sortInfo,
    setSortInfo,
    isFederatedSsoEnabled,
    isLoading,
}: Props) {
    const classes = useStyles();
    const {
        email,
        isSiteEmployee,
        isReadOnlyUser,
    } = useAuth();
    const { showSiteColumn, sitesAreLoading, getSiteName } = useSiteColumn({ dealerId, orgId, siteId });
    const { employeeApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const { showConfirmDialog } = useConfirmDialog();
    const [employeeToEdit, setEmployeeToEdit] = useState<Employee | undefined>();
    const [deletingId, setDeletingId] = useState<number | undefined>();

    const deleteEmployee = (employee: Employee) => {
        showConfirmDialog(`Are you sure you want to delete "${employee.fullName}" employee?`)
            .then((confirmed) => {
                if (!confirmed) return;
                setDeletingId(employee.id);
                employeeApi.delete(employee.id).then((r) => {
                    if (r.result === AjaxResult.Success) {
                        enqueueSnackbar(`Successfully deleted "${employee.fullName}" employee`, { variant: "success" });
                        onLoadEmployees();
                    } else if (r.result === AjaxResult.Forbidden) {
                        enqueueSnackbar(`You do not have permission to delete "${employee.fullName}" employee`, { variant: "error" });
                    } else {
                        enqueueSnackbar(`Failed to delete "${employee.fullName}" employee`, { variant: "error" });
                    }
                    setDeletingId(undefined);
                });
            });
    };

    const sortableHeader = (sortKey: string, label: string) => (
        <TableCellWithSort
            sortKey={sortKey}
            sortInfo={sortInfo}
            setSortInfo={setSortInfo}
            label={label}
        />
    );

    const columns: Column<Employee>[] = [
        { key: 'fullName', label: 'Name', primary: true, headerRender: () => sortableHeader('fullName', 'Name') },
        { key: 'id', label: 'Employee ID', headerRender: () => sortableHeader('id', 'Employee ID') },
        { key: 'phoneNumber', label: 'Phone', hideWhenEmpty: true, headerRender: () => sortableHeader('phoneNumber', 'Phone') },
        { key: 'email', label: 'Email', hideWhenEmpty: true, headerRender: () => sortableHeader('email', 'Email') },
        { key: 'address', label: 'Address', hideWhenEmpty: true, headerRender: () => sortableHeader('address', 'Address') },
        {
            key: 'roles',
            label: 'Account Type',
            headerRender: () => sortableHeader('roles', 'Account Type'),
            render: (e) => e.roles.join(','),
        },
        {
            key: 'lastLoginDate',
            label: 'Last Login',
            hideWhenEmpty: true,
            headerRender: () => sortableHeader('lastLoginDate', 'Last Login'),
            render: (e) => (e.lastLoginDate ? moment(e.lastLoginDate).format('M/DD/YYYY h:mm:ss A') : ''),
        },
        ...(showSiteColumn
            ? [{
                key: 'site',
                label: 'Site',
                headerRender: () => (
                    <TableCell>
                        <div className={classes.siteColumnHeader}>
                            <span>Site</span>
                            {sitesAreLoading && <CircularProgress size={12} />}
                        </div>
                    </TableCell>
                ),
                render: (e: Employee) => getSiteName(e.siteId),
            } as Column<Employee>]
            : []),
        ...(!isReadOnlyUser ? [{
            key: 'biometrics',
            label: 'Biometrics',
            render: (e: Employee) => {
                const enrollDisabled =
                    e.isInactive
                    || orgId === undefined
                    || (isSiteEmployee && email !== e.email)
                    || e.roles.indexOf(UserLevel.OrgEmployee) !== -1;
                return (
                    <EnrollEmployeeButton
                        employee={e}
                        orgId={orgId}
                        siteId={siteId}
                        disabled={enrollDisabled}
                        compact
                    />
                );
            },
        } as Column<Employee>] : []),
    ];

    const rowActions = !isReadOnlyUser
        ? (e: Employee) => {
            const editDisabled =
                email === e.email
                || !e.isEditable
                || (isSiteEmployee && e.roles.includes(UserLevel.SiteAdmin));
            const deleteDisabled =
                email === e.email
                || (isSiteEmployee && e.roles.includes(UserLevel.SiteAdmin))
                || deletingId === e.id;
            const menuActions: RowAction[] = [
                {
                    label: "Edit",
                    onClick: () => setEmployeeToEdit(e),
                    disabled: editDisabled,
                    hidden: isFederatedSsoEnabled,
                },
                {
                    label: "Delete",
                    onClick: () => deleteEmployee(e),
                    dangerous: true,
                    disabled: deleteDisabled,
                    dividerBefore: true,
                },
            ];
            return <RowActionsMenu actions={menuActions} ariaLabel={`Actions for ${e.fullName}`} />;
        }
        : undefined;

    return (
        <>
            <ResponsiveDataView
                rows={employees}
                columns={columns}
                rowKey={(e) => e.id}
                actions={rowActions}
                isInactive={(e) => !!e.isInactive}
                loading={isLoading}
            />
            {employeeToEdit && (
                <EmployeeEditForm
                    closeEditForm={() => setEmployeeToEdit(undefined)}
                    employee={employeeToEdit}
                    onSaved={onLoadEmployees}
                />
            )}
        </>
    );
}

const useStyles = makeStyles(() => ({
    siteColumnHeader: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        minWidth: "100px",
    },
}));
